import React, { useState, useContext, useEffect, useRef } from 'react';
import AuthContext from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import axios from 'axios';
import { API_URL } from "../config";

function useDebouncedCallback(callback, delay) {
  const timeoutRef = useRef();
  return (...args) => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      callback(...args);
    }, delay);
  };
}

const UserSettings = () => {
  const { currentUser, setCurrentUser, refreshUser } = useContext(AuthContext);
  const { addToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    company_name: '',
    profile_picture: null
  });
  const [previewUrl, setPreviewUrl] = useState(null);
  const [lastSaved, setLastSaved] = useState({});

  useEffect(() => {
    if (currentUser) {
      setFormData(prev => ({
        ...prev,
        full_name: currentUser.name || '',
        email: currentUser.email || '',
        company_name: currentUser.company_name || '',
      }));
      setLastSaved({
        full_name: currentUser.name || '',
        email: currentUser.email || '',
        company_name: currentUser.company_name || '',
        profile_picture: currentUser.profile_picture || null,
      });
      if (currentUser.profile_picture) {
        setPreviewUrl(`${API_URL}${currentUser.profile_picture}`);
      }
    }
  }, [currentUser]);

  const debouncedSave = useDebouncedCallback((data) => {
    handleSubmit(data, false);
  }, 400);

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (name === 'profile_picture' && files && files[0]) {
      const file = files[0];
      setFormData(prev => ({
        ...prev,
        [name]: file
      }));
      // Create preview URL
      const fileUrl = URL.createObjectURL(file);
      setPreviewUrl(fileUrl);
      // Auto-save on file select
      debouncedSave({ ...formData, profile_picture: file });
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    // Only save if value changed
    if (formData[name] !== lastSaved[name]) {
      debouncedSave(formData);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleBlur(e);
    }
  };

  const handleSubmit = async (data, silent = false) => {
    setLoading(true);
    try {
      const formDataToSend = new FormData();
      formDataToSend.append('name', data.full_name);
      formDataToSend.append('email', data.email);
      formDataToSend.append('company_name', data.company_name);
      if (data.profile_picture) {
        formDataToSend.append('profile_picture', data.profile_picture);
      }
      await axios.patch(`${API_URL}/api/auth/user/`, formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      setLastSaved({
        full_name: data.full_name,
        email: data.email,
        company_name: data.company_name,
        profile_picture: data.profile_picture,
      });
      refreshUser();
      if (!silent) addToast('Perfil actualizado', 'success');
    } catch (err) {
      let errorMsg = 'Error al actualizar el perfil';
      if (err.response?.data?.detail && typeof err.response.data.detail === 'string') {
        errorMsg = err.response.data.detail;
      } else if (err.message && typeof err.message === 'string') {
        errorMsg = err.message;
      }
      if (!silent) addToast(errorMsg, 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="max-w-3xl mx-auto">
        <h1 className="text-2xl font-semibold text-gray-900 mb-6">Configuración de la cuenta</h1>
        <div className="bg-white shadow rounded-lg p-6">
          <form className="space-y-6" autoComplete="off">
            {/* Profile picture row refined for perfect alignment */}
            <div className="flex items-center border-b border-gray-200 min-h-[48px] px-1">
              <label className="block text-sm font-medium text-gray-700 flex-1">Profile picture</label>
              <div className="flex items-center">
                <div className="relative">
                  {previewUrl ? (
                    <img
                      src={previewUrl}
                      alt="Profile"
                      className="h-10 w-10 rounded-full object-cover border border-gray-300"
                    />
                  ) : (
                    <div className="h-10 w-10 rounded-full bg-pink-400 flex items-center justify-center text-white text-base font-bold border border-gray-300">
                      {formData.full_name ? formData.full_name.slice(0, 2).toUpperCase() : 'US'}
                    </div>
                  )}
                  <label
                    htmlFor="profile_picture"
                    className="absolute bottom-0 right-0 bg-white rounded-full p-1 shadow-lg cursor-pointer border border-gray-300"
                    style={{ transform: 'translate(25%, 25%)' }}
                  >
                    <svg
                      className="h-4 w-4 text-gray-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                    </svg>
                  </label>
                  <input
                    type="file"
                    id="profile_picture"
                    name="profile_picture"
                    accept="image/*"
                    onChange={handleChange}
                    className="hidden"
                  />
                </div>
              </div>
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email
              </label>
              <input
                type="email"
                name="email"
                id="email"
                value={formData.email}
                onChange={handleChange}
                onBlur={handleBlur}
                onKeyDown={handleKeyDown}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                autoComplete="email"
              />
            </div>

            <div>
              <label htmlFor="full_name" className="block text-sm font-medium text-gray-700">
                Full name
              </label>
              <input
                type="text"
                name="full_name"
                id="full_name"
                value={formData.full_name}
                onChange={handleChange}
                onBlur={handleBlur}
                onKeyDown={handleKeyDown}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                autoComplete="name"
              />
            </div>

            <div>
              <label htmlFor="company_name" className="block text-sm font-medium text-gray-700">
                Company name
              </label>
              <input
                type="text"
                name="company_name"
                id="company_name"
                value={formData.company_name}
                onChange={handleChange}
                onBlur={handleBlur}
                onKeyDown={handleKeyDown}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default UserSettings; 